// A case's Monitor (PRD-0075 B, ADR-0096): what is watched and the alerts it raised.
// Entities are named by their stable `_uid` (ADR-0091), never by a Neo4j element id.
import type { CaseRole } from './cases'
import { apiClient } from './client'

/** A watched entity (`entity_uid`) or phrase; `label`/`entity_type` as they were when added. */
export interface WatchItemDto {
  id: string
  watchlist_id: string
  entity_uid: string | null
  phrase: string | null
  label: string | null
  entity_type: string | null
  created_at: string
}

export interface WatchRuleDto {
  id: string
  watchlist_id: string
  /** `occurrence`, `phrase`, `new_connection` or `state_change`. */
  kind: string
  severity: string
  /** `{ phrases: string[] }` for a phrase rule; empty for the others. */
  config: Record<string, unknown>
  created_at: string
}

export type RuleSeverity = 'low' | 'medium' | 'high'

/**
 * A rule as the Monitor page makes and saves it (`NamedRuleWrite`, ADR-0100): a list of its own,
 * named. A phrase rule takes `phrases`; a new-connection or state-change rule takes `entities`.
 */
export interface NamedRuleWrite {
  name: string
  kind: 'phrase' | 'new_connection' | 'state_change'
  severity: RuleSeverity
  phrases: string[]
  entities: WatchEntityRequest[]
  /** A new-connection or state-change rule over every entity the case watches. */
  all_watched: boolean
}

/** How the inbox is ordered: newest learned first, or the most severe first. */
export type AlertSort = 'date' | 'severity'

export interface WatchlistDto {
  id: string
  name: string
  /** The list *Watch* on an entity adds to; the UI names it in the reader's language. */
  is_default: boolean
  created_at: string
  items: WatchItemDto[]
  rules: WatchRuleDto[]
}

export interface WatchlistsResponse {
  watchlists: WatchlistDto[]
  /** The reader's role: a viewer is shown no watch toggle and no remove. */
  member_role: CaseRole
}

/** One inbox row as `GET /v1/cases/{case_id}/monitor/alerts` answers it (`AlertOut`). */
export interface AlertDto {
  id: string
  rule_id: string
  rule_kind: string
  severity: string
  watchlist_id: string
  /** The rule's name, its list's (ADR-0100); null for the default list, which the UI names. */
  rule_name: string | null
  /** The watched entity; a new connection's first end; null for a phrase. */
  subject_uid: string | null
  /** The watched item's label, else the name the source used. */
  subject_label: string | null
  subject_type: string | null
  /** A new connection's other end. */
  object_uid?: string | null
  /**
   * What the rule found: `{phrase, samples}`, `{types, subject_name, object_name}`,
   * `{changes: [{key, old, new}], subject_name}` or `{merged: [{uid, name}], reason, subject_name}`.
   */
  detail?: Record<string, unknown>
  document_id: string | null
  document_name: string | null
  source_ref: Record<string, unknown>
  hits: number
  sample: string | null
  /** When the case learned the source; ISO 8601. */
  recorded_at: string
  /** When the alert was raised: the bell counts those raised since the reader's last visit. */
  created_at: string
  /** `new`, `accepted`, `dismissed` or `snoozed`; a snooze that has run out reads `new`. */
  state: string
  /** Who triaged it last and when. */
  triaged_by?: string | null
  triaged_by_name?: string | null
  triaged_at?: string | null
  snoozed_until?: string | null
  /** The investigation an accepted alert went into, while it exists. */
  investigation_id?: string | null
  investigation_title?: string | null
}

export interface AlertsResponse {
  alerts: AlertDto[]
}

/** An alert's triage state; a snoozed one whose time has passed reads `new`. */
export type AlertState = 'new' | 'accepted' | 'dismissed' | 'snoozed'

/** The inbox read: how many alerts, newest learned first. A type, not an interface: it is a query. */
export type AlertQuery = {
  /** At most `MAX_INBOX_ALERTS`, as the server keeps it. */
  limit?: number
}

/** The most alerts one read of the inbox takes (`MAX_ALERTS_LIMIT` on the server). */
export const MAX_INBOX_ALERTS = 500

/** How an alert is triaged (`POST …/alerts/{id}/triage`). */
export type TriageRequest =
  | { action: 'accept'; investigation_id: string }
  | { action: 'accept'; new_investigation_title: string }
  | { action: 'dismiss' }
  | { action: 'snooze'; until: string }
  | { action: 'reopen' }

/** The reader's visit to the case's Monitor, and the one before it (null: the first). */
export interface MonitorSeenDto {
  seen_at: string
  previous_seen_at: string | null
}

/** The bell: alerts raised since the reader's last visit. */
export interface MonitorUnreadDto {
  count: number
  seen_at: string | null
}

/** A channel and folder the case's sources keep coming through (`GET …/monitor/sources`). */
export interface StandingSourceDto {
  /** `upload`, `sftp`, `lake_import` or `humint`. */
  channel: string
  /** Slash-separated, day folders left out; "" is the case's root. */
  folder: string
  documents: number
  last_received_at: string
  last_document_id: string | null
  last_document_name: string | null
}

export interface StandingSourcesResponse {
  sources: StandingSourceDto[]
}

export interface WatchEntityRequest {
  uid: string
  label: string | null
  entity_type: string | null
}

const base = (caseId: string) => `/v1/cases/${caseId}/monitor`

export const caseMonitorApi = {
  listWatchlists: (caseId: string, signal?: AbortSignal) =>
    apiClient.get<WatchlistsResponse>(`${base(caseId)}/watchlists`, { signal }),
  /** The inbox, newest learned first or (`sort: 'severity'`) the most severe first. */
  listAlerts: (caseId: string, signal?: AbortSignal, query: AlertQuery = {}) =>
    apiClient.get<AlertsResponse>(`${base(caseId)}/alerts`, { signal, params: query }),
  /** A rule on a list of its own, named; *Mentioned in a new source* comes with *Watch* instead. */
  createRule: (caseId: string, body: NamedRuleWrite) =>
    apiClient.post<WatchlistDto>(`${base(caseId)}/rules`, body),
  /** The rule, its name and what it watches as `body` has them; its kind stays. */
  saveRule: (caseId: string, ruleId: string, body: NamedRuleWrite) =>
    apiClient.put<WatchlistDto>(`${base(caseId)}/rules/${ruleId}`, body),
  /** Hides the rule (and its list, once empty); its alerts stay in the inbox. */
  deleteRule: (caseId: string, ruleId: string) =>
    apiClient.delete<void>(`${base(caseId)}/rules/${ruleId}`),
  /** *Watch*: onto the case's default list, which carries the occurrence rule. */
  watch: (caseId: string, body: WatchEntityRequest) =>
    apiClient.post<WatchItemDto>(`${base(caseId)}/watched`, body),
  /** *Watched* pressed again: off every live list of the case. */
  unwatch: (caseId: string, uid: string) =>
    apiClient.delete<void>(`${base(caseId)}/watched/${encodeURIComponent(uid)}`),
  /**
   * One triage for one alert or a group of them (one rule in one source); an accept into a new
   * investigation makes it once. Answers the alerts as triaged.
   */
  triageAll: (caseId: string, alertIds: readonly string[], triage: TriageRequest) =>
    apiClient.post<AlertsResponse>(`${base(caseId)}/alerts/triage`, {
      alert_ids: alertIds,
      triage,
    }),
  /** The reader opened the case's Monitor: their bell goes to zero. */
  markSeen: (caseId: string) => apiClient.put<MonitorSeenDto>(`${base(caseId)}/seen`),
  unread: (caseId: string, signal?: AbortSignal) =>
    apiClient.get<MonitorUnreadDto>(`${base(caseId)}/unread`, { signal }),
  listSources: (caseId: string, signal?: AbortSignal) =>
    apiClient.get<StandingSourcesResponse>(`${base(caseId)}/sources`, { signal }),
  /** Server-Sent Events of the case's Monitor (`ready`, `alerts_raised`, `alert_triaged`). */
  streamUrl: (caseId: string) => `/api${base(caseId)}/stream`,
}
